import { RBAC_DEFAULT_DISABLED_MESSAGE } from '../../shared/constants';
import { GlobalAction } from '../../shared/interfaces/allowed-actions';
import { Destination } from '../../shared/interfaces/destination';
import { allowedActionsService } from '../../shared/services/allowed-actions.service';
import { DestinationListReducerState } from './cache/interface';
import { DestinationListTable } from './components/DestinationListTable';
import { useInMemoryFilteredList } from '../../shared/hooks/useInMemoryFilteredList';
import { DestinationAPI } from '../api/DestinationAPI';
import { DestinationListFactory } from './factories/destinationFactory';
import { DESTINATION_LIST_SEARCH_FILTER_KEY } from './constants';
import { matchPattern } from '../../shared/utils/string/pattern';
import { EntityListNonIdleProps } from '../../entity-list/interface';
import { EntityList } from '../../entity-list/EntityList';
import { DestinationIntro } from './components/DestinationIntro';
import { CreateDestinationButton } from './components/CreateDestinationButton';
import { useAnalyticsTracker } from '../../tracking/hooks/useAnalyticsTracker';
import { DestinationTrackers } from '../tracking/destination-trackers';
import { EntityListEntities } from '../../shared/interfaces/entity-list';
import { getSearchValue } from '../../shared/utils/filters';
import { ListConductorRawFilter } from '../../list-conductor/filters';

export type DestinationListProps = {
  dataId?: string;
  onDestinationSelect?: (destination: Destination) => void;
  onStateChange?: (state: DestinationListReducerState) => void;
  initialData?: Partial<DestinationListReducerState>;
  wizardMode?: boolean;
} & EntityListNonIdleProps;

export function DestinationList({
  dataId,
  initialData,
  wizardMode,
  onDestinationSelect,
  onStateChange,
  newListComponentProps
}: DestinationListProps) {
  const { eventTrack } = useAnalyticsTracker();

  const isCreateDestinationActionDisabled =
    allowedActionsService.getActionByType(GlobalAction.CREATE_DESTINATION)?.is_disabled ?? true;
  const createDestinationActionDisabledReason =
    allowedActionsService.getActionByType(GlobalAction.CREATE_DESTINATION)?.disabled_reason ??
    RBAC_DEFAULT_DISABLED_MESSAGE;

  const { data, fetch, reset } = useInMemoryFilteredList(
    async () => {
      const res = await DestinationAPI.getDestinations();
      return DestinationListFactory(res.data);
    },
    (destination, params) => {
      return matchPattern(
        [destination.name, destination.destinationType.displayName],
        getSearchValue(
          (params?.filters || []) as ListConductorRawFilter[],
          DESTINATION_LIST_SEARCH_FILTER_KEY
        )
      );
    },
    initialData?.destinations
  );

  return (
    <EntityList
      entity={EntityListEntities.DESTINATION}
      dataId={dataId}
      mode='standalone'
      dataSource$={fetch}
      listIcon='destinations'
      entityTitle={!wizardMode ? 'Destinations' : undefined}
      emptyTitle='No Destinations Found'
      emptySubTitle='Modify search term to find the desired destination'
      searchKey={DESTINATION_LIST_SEARCH_FILTER_KEY}
      showSearch
      inMemoryList
      onRefresh={() => {
        eventTrack({
          action: DestinationTrackers.DESTINATION_TRACKER_DESTINATION_LIST_REFRESH_CLICK
        });
        reset();
      }}
      shimmerColumnsCount={5}
      newListConnectedTitle='No Destination Configured'
      NewListComponent={!wizardMode ? DestinationIntro : undefined}
      searchPlaceholder='Search Destinations'
      genericErrorTitle='Error while fetching Destinations'
      genericErrorSubTitle='Retry Loading the Destinations'
      ListComponent={DestinationListTable}
      listComponentProps={{
        onDestinationSelect
      }}
      StandaloneModeActions={!wizardMode ? CreateDestinationButton : undefined}
      standaloneModeActionsProps={{
        disabled: isCreateDestinationActionDisabled,
        disabledReason: createDestinationActionDisabledReason
      }}
      onStateChange={state => {
        if (typeof onStateChange === 'function') {
          onStateChange({
            filters: state.filters,
            destinations: data
          });
        }
      }}
      newListComponentProps={newListComponentProps}
    />
  );
}
